import { FC, memo, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ContentHeader from "../../components/reusable-components/dashboard-contents/content-header/ContentHeader";
import DataTable from "../../components/reusable-components/data-table/DataTable";
import TableCard from "../../components/reusable-components/data-table/TableCard";
import TableFooter from "../../components/reusable-components/data-table/TableFooter";
import TableToolbar from "../../components/reusable-components/data-table/TableToolbar";
import SaveFilterButton from "../../components/reusable-components/save-filter-button/SaveFilterButton";
import IsMobile from "../../hooks/isMobile";
import useUserInfo from "../../hooks/reduxHelpersHooks/useUserInfo";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import useHiddenColumns from "../../hooks/useHiddenColumns";
import useListTable from "../../hooks/useListTable";
import useSavedFilters from "../../hooks/useSavedFilters";
import { resetEventDetail } from "../../store/events/eventSlice";
import { iEventDetailCMS, iEventProp, iFlatEvent } from "../../types/eventTypes";
import MobileEventList from "./MobileEventList";
import { getColumns, getFlatEvent } from "./utill";
import { getEventFilterDefs } from "./eventFilters";
import PrePaidRequests from "../../components/pages-components/pre-paid-requests/PrePaidRequests";
import { GridTypesEnum } from "../../constants/enums";
import { getLoggedInVendorDisplayName } from "../../helpers/sendbirdDisplay";

const EventGrid: FC<iEventProp> = memo((props) => {
	const { gridId, showSave, title, queryParams, isDeclinedGrid, showPrepaid } = props;
	const eventList = useAppSelector((state) => state.events.eventList);
	const eventRequests = useAppSelector((state) => state.events.eventRequests);
	const cms = useAppSelector((state) => state.events.cms);
	const isLoading = useAppSelector((state) => state.events.isLoadingEventList);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isMobile = IsMobile();
	const { userDetails } = useUserInfo();
	const { hiddenColumns, onToggleColumn, columnOrder, onColumnOrderChange, pinnedColumns, onTogglePin } =
		useHiddenColumns(gridId);

	const getFromCms = useCallback(
		(value: string | null) => (value && cms[value as keyof iEventDetailCMS]) || "",
		[cms],
	);

	const rows = useMemo(
		() => getFlatEvent(eventList, getFromCms, isDeclinedGrid) as iFlatEvent[],
		[eventList, getFromCms, isDeclinedGrid],
	);
	const columns = useMemo(() => getColumns(isDeclinedGrid), [isDeclinedGrid]);
	const filterDefs = useMemo(() => getEventFilterDefs(columns.map((column) => column.key)), [columns]);
	const table = useListTable({ rows, columns, filterDefs });
	// Only lists that offer Save Filter load saved filters; the others always open unfiltered, as before.
	const { save, saving, canSave, clearSaved } = useSavedFilters({
		gridId: showSave ? gridId : undefined,
		gridType: GridTypesEnum.Event,
		setItems: table.setItems,
		withCmsMeta: true,
	});

	const openEvent = useCallback(
		(row: iFlatEvent) => {
			dispatch(resetEventDetail());
			setTimeout(() => {
				navigate(`/event-detail/${row.id}?type=${queryParams}`);
			}, 100);
		},
		[dispatch, navigate, queryParams],
	);

	return (
		<>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<Box className="content">
				<Box className="pageLayout" sx={{ padding: isMobile ? "16px" : "30px 30px 16px" }}>
					{showPrepaid && eventRequests?.length > 0 && <PrePaidRequests eventRequests={eventRequests} />}
					<ContentHeader
						contentHeaderTitle={title}
						welcomeTitleName={getLoggedInVendorDisplayName(userDetails) ?? undefined}
						filterBtn={table.hasFilters && !isMobile}
						applyfiltersActiveClass={table.hasFilters ? "apply_filter_active" : ""}
					/>
					{isMobile ? (
						<>
							<Box sx={{ bgcolor: "#fff", borderRadius: "16px" }}>
								<TableToolbar
									{...table.toolbarProps}
									onClearAll={() => {
										table.toolbarProps.onClearAll();
										clearSaved();
									}}
									compact
									searchPlaceholder="Search events"
									filterActions={
										showSave ? (
											<SaveFilterButton onClick={() => save(table.items)} saving={saving} disabled={!canSave(table.items)} />
										) : undefined
									}
								/>
							</Box>
							<MobileEventList eventList={table.rows} isLoading={isLoading} queryParams={queryParams} />
						</>
					) : (
						<Box className="vender_table">
							<TableCard
								toolbar={
									<TableToolbar
										{...table.toolbarProps}
										onClearAll={() => {
											table.toolbarProps.onClearAll();
											clearSaved();
										}}
										searchPlaceholder="Search events"
										filterActions={
											showSave ? (
												<SaveFilterButton onClick={() => save(table.items)} saving={saving} disabled={!canSave(table.items)} />
											) : undefined
										}
										columns={columns.filter((column) => !column.fixed).map(({ key, label }) => ({ key, label }))}
										hiddenColumns={hiddenColumns}
										onToggleColumn={onToggleColumn}
										columnOrder={columnOrder}
										onColumnOrderChange={onColumnOrderChange}
										pinnedColumns={pinnedColumns}
										onTogglePin={onTogglePin}
									/>
								}
							>
								<DataTable
									{...table.tableProps}
									columns={columns}
									rows={table.pageRows}
									getRowId={(row) => row.id}
									rowIndicator={(row) => Boolean(row.showIndicator)}
									loading={isLoading && rows.length === 0}
									emptyMessage={rows.length === 0 ? "No Events" : "No events match these filters."}
									hiddenColumns={hiddenColumns}
									columnOrder={columnOrder}
									onColumnOrderChange={onColumnOrderChange}
									pinnedColumns={pinnedColumns}
									onRowClick={openEvent}
									minWidth={1200}
								/>
								<TableFooter {...table.footerProps} />
							</TableCard>
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
});
export default EventGrid;
